import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../lib/auth.js';
import { api, ApiError } from '../lib/api.js';
import { motion } from 'framer-motion';
import { CompanyAnalyticsView } from '../components/CompanyAnalyticsView.js';

interface Stats {
  form9_total: number;
  form10_total: number;
  form11_total?: number;
  equipment_total: number;
  companies_total: number;
  training_total: number;
  overdue: number;
  expiring_soon: number;
}

const STAT_CARDS = [
  { label: 'Form 9 Certificates', key: 'form9_total' as keyof Stats, color: 'brand', icon: '📋' },
  { label: 'Form 10 Certificates', key: 'form10_total' as keyof Stats, color: 'brand', icon: '📋' },
  { label: 'Form 11 Reports', key: 'form11_total' as keyof Stats, color: 'purple', icon: '🧪' },
  { label: 'Equipment', key: 'equipment_total' as keyof Stats, color: 'blue', icon: '⚙️' },
  { label: 'Companies', key: 'companies_total' as keyof Stats, color: 'green', icon: '🏭' },
] as const;

interface PersonnelComplianceItem {
  id: string;
  full_name: string;
  designation: string;
  qualification?: string;
  competency_cert_number?: string;
  competency_valid_upto?: string;
  licence_status: 'EXPIRED' | 'EXPIRING_SOON' | 'VALID' | 'NO_DATE';
  is_expired: boolean;
  is_expiring_soon: boolean;
  days_remaining: number;
  formatted_valid_upto: string;
}

interface PersonnelComplianceData {
  expired: PersonnelComplianceItem[];
  expiring_soon: PersonnelComplianceItem[];
  valid: PersonnelComplianceItem[];
  summary: {
    total: number;
    expired: number;
    expiring_soon: number;
    valid: number;
  };
}

export function DashboardPage() {
  const { canWrite } = useAuth();
  const [activeSection, setActiveSection] = useState<'system' | 'company'>('system');
  const [stats, setStats] = useState<Stats | null>(null);
  const [personnelCompliance, setPersonnelCompliance] = useState<PersonnelComplianceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.dashboard.stats().then((r) => setStats((r.data as unknown) as Stats ?? null)),
      api.dashboard.personnelCompliance().then((r) => setPersonnelCompliance((r.data as unknown) as PersonnelComplianceData ?? null)),
    ])
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;
  if (error) return <ErrorState message={error} />;
  if (!stats) return null;

  const hasExpired = (personnelCompliance?.summary.expired ?? 0) > 0;
  const hasExpiringSoon = (personnelCompliance?.summary.expiring_soon ?? 0) > 0;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-1 pb-12">
      {/* ── Top Header & Analytics View Switcher ─────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-gray-200">
        <div>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Compliance &amp; Analytics Dashboard</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {activeSection === 'system'
              ? 'High-level compliance overview, inspection metrics, and competent person licensing status.'
              : 'Company-wise certificate volumes, renewal intervals, and financial year activity summary.'}
          </p>
        </div>

        <div className="flex items-center gap-1 bg-slate-200/80 p-1 rounded-xl shadow-inner self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setActiveSection('system')}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeSection === 'system'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>📊</span>
            <span>System Overview</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveSection('company')}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeSection === 'company'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>🏢</span>
            <span>Company Analytics</span>
          </button>
        </div>
      </div>

      {activeSection === 'company' ? (
        <CompanyAnalyticsView />
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STAT_CARDS.map((card, i) => (
              <motion.div
                key={card.key}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="card p-5"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{card.label}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {String(stats[card.key] ?? 0)}
                    </p>
                  </div>
                  <span className="text-2xl">{card.icon}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Compliance overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ComplianceCard
              title="Overdue Inspections"
              count={stats.overdue}
              variant="red"
              linkTo="/renewals?status=OVERDUE"
            />
            <ComplianceCard
              title="Expiring in 30 Days"
              count={stats.expiring_soon}
              variant="orange"
              linkTo="/renewals?status=EXPIRING_SOON"
            />
          </div>

          {/* Quick links */}
          {(canWrite('form9') || canWrite('form10') || canWrite('form11') || canWrite('training') || canWrite('equipment') || canWrite('companies')) && (
            <div className="card p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="flex flex-wrap gap-3">
                {canWrite('form9') && <Link to="/form9" className="btn btn-primary">New Form 9</Link>}
                {canWrite('form10') && <Link to="/form10" className="btn btn-primary">New Form 10</Link>}
                {canWrite('form11') && <Link to="/form11" className="btn btn-primary bg-purple-600 hover:bg-purple-700">New Form 11</Link>}
                {canWrite('training') && <Link to="/training" className="btn btn-primary">New Training Cert</Link>}
                {canWrite('equipment') && <Link to="/equipment" className="btn btn-secondary">Add Equipment</Link>}
                {canWrite('companies') && <Link to="/companies" className="btn btn-secondary">Add Company</Link>}
              </div>
            </div>
          )}

          {/* Competent Person Licence Compliance & Expiry Notifications Section */}
          <div className="card p-5 sm:p-6 space-y-5 border-t-4 border-t-slate-800">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2.5">
                <span className="p-2 rounded-lg bg-slate-100 text-slate-700 text-lg">🛡️</span>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Competent Person Licence Compliance</h2>
                  <p className="text-xs text-gray-500">
                    Licence validity monitor & certificate issuance governance
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to="/personnel"
                  className="text-xs font-semibold text-brand-600 hover:text-brand-800 bg-brand-50 hover:bg-brand-100 px-3 py-1.5 rounded-lg border border-brand-200 transition-colors inline-flex items-center gap-1"
                >
                  Manage Personnel →
                </Link>
              </div>
            </div>

            {/* Live Notification Banners */}
            {hasExpired && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4 space-y-3 shadow-xs">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="text-xl leading-none mt-0.5">🔴</span>
                    <div>
                      <h3 className="text-sm font-bold text-red-900">
                        URGENT: {personnelCompliance?.summary.expired} Competent Person Licence Expired
                      </h3>
                      <p className="text-xs text-red-700 mt-1 leading-relaxed">
                        Certificate generation (Form 10, Form 11, Training) is <strong>strictly blocked</strong> for expired personnel. Please update licence validity dates to restore certificate issuance.
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/personnel"
                    className="btn btn-sm bg-red-600 hover:bg-red-700 text-white text-xs whitespace-nowrap shadow-xs"
                  >
                    Renew Licences
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 pt-1">
                  {personnelCompliance?.expired.map((p) => (
                    <div key={p.id} className="bg-white/90 rounded-lg p-2.5 border border-red-200 text-xs flex flex-col justify-between">
                      <div>
                        <p className="font-bold text-gray-900">{p.full_name}</p>
                        <p className="text-[11px] text-gray-500 font-mono">{p.competency_cert_number || 'No Cert No'}</p>
                      </div>
                      <div className="mt-2 pt-2 border-t border-red-100 flex items-center justify-between">
                        <span className="text-[11px] font-bold text-red-700">Expired: {p.formatted_valid_upto}</span>
                        <span className="text-[10px] font-semibold bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                          {Math.abs(p.days_remaining)}d ago
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasExpiringSoon && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 space-y-3 shadow-xs">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="text-xl leading-none mt-0.5">⚠️</span>
                    <div>
                      <h3 className="text-sm font-bold text-amber-900">
                        WARNING: {personnelCompliance?.summary.expiring_soon} Competent Person Licences Expiring Soon (&le; 30 Days)
                      </h3>
                      <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                        The following competent person licences will expire within the next 30 days. Plan renewals in advance to prevent service disruptions.
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/personnel"
                    className="btn btn-sm bg-amber-600 hover:bg-amber-700 text-white text-xs whitespace-nowrap shadow-xs"
                  >
                    View Details
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 pt-1">
                  {personnelCompliance?.expiring_soon.map((p) => (
                    <div key={p.id} className="bg-white/90 rounded-lg p-2.5 border border-amber-200 text-xs flex flex-col justify-between">
                      <div>
                        <p className="font-bold text-gray-900">{p.full_name}</p>
                        <p className="text-[11px] text-gray-500 font-mono">{p.competency_cert_number || 'No Cert No'}</p>
                      </div>
                      <div className="mt-2 pt-2 border-t border-amber-100 flex items-center justify-between">
                        <span className="text-[11px] font-bold text-amber-800">Expires: {p.formatted_valid_upto}</span>
                        <span className="text-[10px] font-bold bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
                          {p.days_remaining}d left
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!hasExpired && !hasExpiringSoon && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xl">🟢</span>
                  <div>
                    <h3 className="text-sm font-bold text-emerald-900">All Competent Person Licences Are Active &amp; Valid</h3>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      {personnelCompliance?.summary.valid ?? 0} competent persons registered with active licence status.
                    </p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  100% Compliant
                </span>
              </div>
            )}

            {/* Overview Table of Competent Persons */}
            {personnelCompliance && (personnelCompliance.summary.total > 0) && (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="table w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                    <tr>
                      <th className="py-2.5 px-3 text-left">Competent Person</th>
                      <th className="py-2.5 px-3 text-left">Cert Number</th>
                      <th className="py-2.5 px-3 text-left">Licence Valid Upto</th>
                      <th className="py-2.5 px-3 text-left">Status</th>
                      <th className="py-2.5 px-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {[
                      ...personnelCompliance.expired,
                      ...personnelCompliance.expiring_soon,
                      ...personnelCompliance.valid,
                    ].map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-2 px-3">
                          <p className="font-semibold text-gray-900">{p.full_name}</p>
                          <p className="text-[11px] text-gray-400">{p.qualification || p.designation}</p>
                        </td>
                        <td className="py-2 px-3 font-mono text-gray-700">
                          {p.competency_cert_number || '—'}
                        </td>
                        <td className="py-2 px-3 font-medium">
                          <span className={p.is_expired ? 'text-red-700 font-bold' : p.is_expiring_soon ? 'text-amber-800 font-bold' : 'text-gray-800'}>
                            {p.formatted_valid_upto}
                          </span>
                        </td>
                        <td className="py-2 px-3 whitespace-nowrap">
                          {p.is_expired ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-red-100 text-red-800 border border-red-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                              Expired ({Math.abs(p.days_remaining)}d ago)
                            </span>
                          ) : p.is_expiring_soon ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200">
                              <span>⚠️</span>
                              Expiring in {p.days_remaining}d
                            </span>
                          ) : p.licence_status === 'NO_DATE' || !p.competency_valid_upto ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                              <span>—</span>
                              Date Not Set
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                              <span>✓</span>
                              Valid ({p.days_remaining}d left)
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          <Link
                            to="/personnel"
                            className="text-xs font-semibold text-brand-600 hover:text-brand-800 hover:underline"
                          >
                            Edit
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ComplianceCard({
  title, count, variant, linkTo,
}: { title: string; count: number; variant: 'red' | 'orange'; linkTo: string }) {
  const bg = variant === 'red' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200';
  const numColor = variant === 'red' ? 'text-red-600' : 'text-orange-600';

  return (
    <div className={`card p-5 border-l-4 ${variant === 'red' ? 'border-l-red-500' : 'border-l-orange-500'}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-3xl font-bold ${numColor} mt-1`}>{count}</p>
        </div>
        <Link to={linkTo} className={`btn btn-secondary ${bg}`}>
          View →
        </Link>
      </div>
    </div>
  );
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}
